/**
 * Shadow-DOM modal controller: quiz steps 1–7, Pathfinder handoff, results from Q1–7 scoring, focus management,
 * and GTM `dataLayer` funnel events (see README “Analytics”).
 */
import { pushDataLayer, type EceModalTrigger } from '../analytics/data-layer';
import type { QuizConfig } from '../model/types';
import { createInitialSelections } from '../state/widget-state';
import { scoreSelections, winningResultFromScores } from '../scoring/compute-archetype';
import { buildPathfinderUrl, subscribePathfinderComplete } from '../pathfinder/iframe-bridge';
import { markIntentGateDeclined, markModalConsumed } from '../session/modal-session';
import type { TrackingParams } from '../utils/tracking';

export interface WidgetControllerOptions {
  config: QuizConfig;
  /** Host inside shadow root (styles live on a sibling `style` element). */
  mount: HTMLElement;
  /** `sessionStorage` key for “modal already seen” (see `sessionStorageKeyForArticle`). */
  sessionKey: string;
  pathfinderBaseUrl: string;
  pathfinderPath: string;
  pathfinderAllowedOrigins: readonly string[];
  /** Query params forwarded to Pathfinder on the iframe URL. */
  tracking: TrackingParams;
  /** Pathfinder `rsid` query value (URL, storage, or generated). */
  rsid: string;
}

export class EceQuizWidgetController {
  private readonly opts: WidgetControllerOptions;
  private questionIndex = 0;
  /** Selected answer index per question, or null */
  private readonly selections = createInitialSelections();
  private pathfinderUnsub: (() => void) | null = null;
  private lastFocus: Element | null = null;
  /** True while exit animation runs so dismiss isn’t run twice. */
  private dialogClosing = false;
  /** In-widget funnel: questions 1–7, Pathfinder iframe, or post-Pathfinder results. */
  private funnelPhase: 'questions' | 'pathfinder' | 'results' = 'questions';
  private quizStartLogged = false;
  /** Pending trigger detail until user accepts the intent gate (passed to `mount`). */
  private pendingIntentDetail: { trigger: EceModalTrigger } | null = null;
  private intentLastFocus: Element | null = null;
  private intentGateTrapCleanup: (() => void) | null = null;
  /**
   * Bumps when the intent gate is dismissed so deferred `rAF` / `queueMicrotask` work from
   * `showIntentGate` cannot run after "Take the Quiz" (re-adding `ece-open` or refocusing the panel).
   */
  private intentGateGeneration = 0;

  constructor(options: WidgetControllerOptions) {
    this.opts = options;
    this.render();
    this.bindShell();
    this.bindIntentGate();
    this.renderQuestionsView();
  }

  /**
   * Opens the intent gate (call from scroll/exit trigger). Full modal opens only after “Take the Quiz”.
   */
  showIntentGate(detail: { trigger: EceModalTrigger }): void {
    this.pendingIntentDetail = detail;
    pushDataLayer({
      event: 'ece_intent_gate_impression',
      modal_trigger: detail.trigger,
      article_url: this.opts.config.articleUrl,
    });
    this.intentLastFocus = document.activeElement;
    const root = this.intentRoot();
    if (!root) return;
    root.classList.remove('ece-open');
    root.hidden = false;
    const gen = ++this.intentGateGeneration;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (gen !== this.intentGateGeneration) return;
        root.classList.add('ece-open');
      });
    });
    queueMicrotask(() => {
      if (gen !== this.intentGateGeneration) return;
      this.setupIntentGateFocusTrap();
      const panel = this.opts.mount.querySelector('.ece-intent-panel') as HTMLElement | null;
      panel?.focus();
    });
  }

  /**
   * Opens the modal (call after intent gate CTA). Marks session consumed, fires analytics, shows Q1.
   */
  mount(detail: { trigger: EceModalTrigger }): void {
    /* Always tear down intent UI so it cannot reappear when the shell closes (belt-and-suspenders). */
    this.hideIntentGateUi();
    markModalConsumed(this.opts.sessionKey);
    /* GTM: modal opened via scroll depth, exit intent, or mobile_scroll_exit (baseline for funnel). */
    pushDataLayer({
      event: 'ece_modal_impression',
      modal_trigger: detail.trigger,
      article_url: this.opts.config.articleUrl,
    });
    this.lastFocus = document.activeElement;
    this.show();
    this.trackQuestionView();
    this.startPathfinderPreload();
    queueMicrotask(() => {
      this.focusTrapStart();
    });
  }

  private shell(): HTMLElement {
    return this.opts.mount.querySelector('[data-ece-shell]') as HTMLElement;
  }

  /** Reveals the dialog root and applies open class after a double rAF (transition entry). */
  private show(): void {
    const el = this.shell();
    this.dialogClosing = false;
    el.classList.remove('ece-open');
    el.hidden = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add('ece-open');
      });
    });
  }

  /**
   * Closes the modal (backdrop, close control, Escape, Done). Runs exit transition when possible; restores focus.
   * Emits `ece_quiz_drop` while still on Q1–7 (see `funnelPhase`).
   */
  dismiss(): void {
    const el = this.shell();
    if (el.hidden) return;
    if (this.dialogClosing) return;

    if (this.funnelPhase === 'questions') {
      /* GTM: user closed before Q7 handoff; last_question_number = step they were on (1–7). */
      pushDataLayer({
        event: 'ece_quiz_drop',
        last_question_number: this.questionIndex + 1,
        article_url: this.opts.config.articleUrl,
      });
    }

    const panel = el.querySelector('.ece-panel') as HTMLElement | null;
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const teardown = () => {
      this.pathfinderUnsub?.();
      this.pathfinderUnsub = null;
      this.clearPathfinderPreload();
      this.mainStage().removeAttribute('aria-hidden');
      /* Intent gate must stay dismissed once the full quiz shell has been used; never leave it under the shell. */
      this.hideIntentGateUi();
      if (this.lastFocus instanceof HTMLElement) {
        this.lastFocus.focus();
      }
    };

    if (!el.classList.contains('ece-open')) {
      el.hidden = true;
      teardown();
      return;
    }

    this.dialogClosing = true;
    let completed = false;
    let fallbackId: number | undefined;

    const finish = () => {
      if (completed) return;
      completed = true;
      this.dialogClosing = false;
      if (panel) {
        panel.removeEventListener('transitionend', onTransitionEnd);
      }
      if (fallbackId !== undefined) {
        window.clearTimeout(fallbackId);
      }
      el.hidden = true;
      teardown();
    };

    const onTransitionEnd = (ev: TransitionEvent) => {
      if (ev.target !== panel) return;
      if (ev.propertyName !== 'opacity' && ev.propertyName !== 'transform') return;
      finish();
    };

    el.classList.remove('ece-open');

    if (reducedMotion) {
      requestAnimationFrame(finish);
      return;
    }

    panel?.addEventListener('transitionend', onTransitionEnd);
    fallbackId = window.setTimeout(finish, 450) as number;
  }

  private intentRoot(): HTMLElement | null {
    return this.opts.mount.querySelector('[data-ece-intent-root]') as HTMLElement | null;
  }

  /** Initial static shell markup (hidden until `mount`). */
  private render(): void {
    const { config } = this.opts;
    const root = this.opts.mount;
    root.innerHTML = `
      <div class="ece-intent-root" data-ece-intent-root hidden>
        <div class="ece-intent-backdrop" data-ece-intent-backdrop tabindex="-1" aria-hidden="true"></div>
        <div
          class="ece-intent-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ece-intent-quiz-title"
          tabindex="-1"
        >
          <div class="ece-intent-head">
            <div class="ece-intent-head-toolbar">
              <button type="button" class="ece-intent-close" data-ece-intent-close aria-label="Close">
                <svg class="ece-intent-close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <p class="ece-intent-headline">Enjoying what you're reading?</p>
            <p class="ece-intent-sub">Here's a quick quiz from Beliefnet specifically for you.</p>
          </div>
          <div class="ece-intent-content">
            <p class="ece-intent-body" id="ece-intent-quiz-title" data-ece-intent-body></p>
            <div class="ece-intent-y-spacer" aria-hidden="true"></div>
            <button type="button" class="ece-btn ece-btn--primary" data-ece-intent-cta>Take the Quiz</button>
          </div>
        </div>
      </div>
      <div class="ece-shell" data-ece-shell hidden>
        <div class="ece-backdrop" data-ece-backdrop tabindex="-1" aria-hidden="true"></div>
        <div
          class="ece-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ece-quiz-title"
        >
          <header class="ece-header">
            <div class="ece-header-text">
              <p class="ece-kicker">Enjoying what you're reading? Here's a quiz for you!</p>
              <h2 id="ece-quiz-title" class="ece-title"></h2>
            </div>
            <button type="button" class="ece-close" data-ece-close aria-label="Close quiz">
              <svg class="ece-close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" />
              </svg>
            </button>
          </header>
          <div class="ece-body">
            <div class="ece-body-stack">
              <div class="ece-main-stage" data-ece-main></div>
              <div class="ece-pathfinder-layer" data-ece-pathfinder-preload aria-hidden="true"></div>
            </div>
          </div>
        </div>
      </div>
    `;
    const titleEl = root.querySelector('.ece-title');
    if (titleEl) titleEl.textContent = config.quizTitle;
    const intentBody = root.querySelector('[data-ece-intent-body]');
    if (intentBody) intentBody.textContent = config.quizTitle;
  }

  /** Intent gate: backdrop, close, CTA, Escape, Tab trap while open. */
  private bindIntentGate(): void {
    const root = this.opts.mount;
    const onDismiss = () => this.dismissIntentGate();
    const onAccept = () => this.acceptIntentGate();
    root.querySelector('[data-ece-intent-backdrop]')?.addEventListener('click', onDismiss);
    root.querySelector('[data-ece-intent-close]')?.addEventListener('click', onDismiss);
    root.querySelector('[data-ece-intent-cta]')?.addEventListener('click', onAccept);
    const panel = root.querySelector('.ece-intent-panel');
    panel?.addEventListener('keydown', (ev: Event) => {
      if (!(ev instanceof KeyboardEvent)) return;
      if (ev.key === 'Escape') {
        ev.preventDefault();
        onDismiss();
      }
    });
  }

  private setupIntentGateFocusTrap(): void {
    this.intentGateTrapCleanup?.();
    const panel = this.opts.mount.querySelector('.ece-intent-panel');
    if (!panel) return;

    const handler: EventListener = (ev: Event) => {
      if (!(ev instanceof KeyboardEvent) || ev.key !== 'Tab') return;
      const focusables = getFocusable(panel);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const rootNode = this.opts.mount.getRootNode();
      const active =
        rootNode instanceof ShadowRoot ? rootNode.activeElement : document.activeElement;
      if (ev.shiftKey) {
        if (active === first || !focusables.includes(active as HTMLElement)) {
          ev.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        ev.preventDefault();
        first.focus();
      }
    };
    panel.addEventListener('keydown', handler);
    this.intentGateTrapCleanup = () => panel.removeEventListener('keydown', handler);
  }

  private hideIntentGateUi(): void {
    this.intentGateGeneration += 1;
    this.intentGateTrapCleanup?.();
    this.intentGateTrapCleanup = null;
    const el = this.intentRoot();
    if (el) {
      el.classList.remove('ece-open');
      el.hidden = true;
    }
  }

  private dismissIntentGate(): void {
    const el = this.intentRoot();
    if (!el || el.hidden) return;
    markIntentGateDeclined(this.opts.sessionKey);
    this.pendingIntentDetail = null;
    this.hideIntentGateUi();
    if (this.intentLastFocus instanceof HTMLElement) {
      this.intentLastFocus.focus();
    }
    this.intentLastFocus = null;
  }

  private acceptIntentGate(): void {
    const detail = this.pendingIntentDetail;
    this.pendingIntentDetail = null;
    this.hideIntentGateUi();
    this.intentLastFocus = null;
    if (!detail) return;
    this.mount(detail);
  }

  /** Close affordances: header button, backdrop click, Escape on the panel. */
  private bindShell(): void {
    const root = this.opts.mount;
    const onClose = () => this.dismiss();
    root.querySelector('[data-ece-close]')?.addEventListener('click', onClose);
    root.querySelector('[data-ece-backdrop]')?.addEventListener('click', onClose);
    root.querySelector('.ece-panel')?.addEventListener('keydown', (ev: Event) => {
      if (!(ev instanceof KeyboardEvent) || ev.key !== 'Escape') return;
      ev.preventDefault();
      onClose();
    });
  }

  /** Main content region: in-widget questions, then Pathfinder iframe, then results HTML. */
  private mainStage(): HTMLElement {
    return this.opts.mount.querySelector('[data-ece-main]') as HTMLElement;
  }

  /** Off-viewport host for the Pathfinder iframe so it can load while Q1–7 run. */
  private pathfinderPreloadHost(): HTMLElement | null {
    return this.opts.mount.querySelector('[data-ece-pathfinder-preload]') as HTMLElement | null;
  }

  private clearPathfinderPreload(): void {
    const host = this.pathfinderPreloadHost();
    if (!host) return;
    host.innerHTML = '';
    host.classList.remove('ece-pathfinder-layer--active');
    host.setAttribute('aria-hidden', 'true');
  }

  /** Same URL as the visible Pathfinder step; used for preload + handoff. */
  private getPathfinderIframeSrc(): string {
    const { tracking, rsid, pathfinderBaseUrl, pathfinderPath } = this.opts;
    return buildPathfinderUrl(pathfinderBaseUrl, pathfinderPath, {
      rsid,
      subid: tracking.subId,
      track1: tracking.track1,
      revenueCode: tracking.revenueCode,
    });
  }

  /**
   * Begins loading Pathfinder in a hidden host as soon as the modal opens so Q7 → path feels instant.
   * Idempotent: skips if an iframe is already present.
   */
  private startPathfinderPreload(): void {
    const host = this.pathfinderPreloadHost();
    if (!host || host.querySelector('iframe')) return;
    const src = this.getPathfinderIframeSrc();
    host.innerHTML = `
      <div class="ece-pathfinder-root">
        <div class="ece-iframe-wrap">
          <iframe
            class="ece-iframe"
            title="Continue your quiz"
            src="${escapeAttr(src)}"
            loading="eager"
            referrerpolicy="no-referrer-when-downgrade"
            tabindex="-1"
          ></iframe>
        </div>
      </div>
    `;
  }

  /**
   * Keeps one `.ece-fa-progress-fill` node so `transition: width` runs (full `innerHTML` on the stage recreates the fill and breaks the animation).
   */
  private ensureQuestionsStageShell(container: HTMLElement): HTMLElement {
    let bodyRoot = container.querySelector('[data-ece-quiz-body]') as HTMLElement | null;
    if (bodyRoot) return bodyRoot;

    container.innerHTML = `
      <div class="ece-fa-progress-wrap" data-ece-quiz-progress aria-live="polite">
        <div
          class="ece-fa-progress-track"
          data-ece-progress-track
          role="progressbar"
          aria-valuemin="1"
          aria-valuemax="7"
          aria-label="Quiz progress, questions 1 through 7"
        >
          <div class="ece-fa-progress-fill" data-ece-progress-fill></div>
        </div>
        <p class="ece-fa-progress-caption">Discovering your archetype...</p>
      </div>
      <div data-ece-quiz-body></div>
    `;
    bodyRoot = container.querySelector('[data-ece-quiz-body]') as HTMLElement;
    return bodyRoot;
  }

  private updateQuestionProgressBar(
    container: HTMLElement,
    progress: number,
    faithBarPercent: number,
    progressLabel: string,
  ): void {
    const track = container.querySelector('[data-ece-progress-track]') as HTMLElement | null;
    const fill = container.querySelector('[data-ece-progress-fill]') as HTMLElement | null;
    track?.setAttribute('aria-valuenow', String(progress));
    track?.setAttribute('aria-valuetext', progressLabel);
    if (fill) {
      fill.style.width = `${faithBarPercent}%`;
    }
  }

  /** Renders current `questionIndex`, wires answer handlers, emits `ece_question_view` when visible. */
  private renderQuestionsView(): void {
    const container = this.mainStage();
    container.hidden = false;
    const { config } = this.opts;
    const q = config.questions[this.questionIndex];
    const progress = this.questionIndex + 1;
    const sel = this.selections[this.questionIndex];

    const optionsHtml = q.answers
      .map(
        (a, i) => `
        <button
          type="button"
          class="ece-option${sel === i ? ' ece-option--selected' : ''}"
          data-ece-option="${i}"
        >
          <span class="ece-option-text">${escapeHtml(a.text)}</span>
        </button>`,
      )
      .join('');

    /* Progress fill uses 70% of the track by Q7 (30% reserved for Pathfinder / extended flow in the product pattern). */
    const faithBarPercent = (progress / 7) * 70;
    const progressLabel = `Question ${progress} of 7`;

    const bodyRoot = this.ensureQuestionsStageShell(container);
    this.updateQuestionProgressBar(container, progress, faithBarPercent, progressLabel);

    bodyRoot.innerHTML = `
      <p class="ece-fa-q-index">${progressLabel}</p>
      <p class="ece-question">${escapeHtml(q.text)}</p>
      <div class="ece-options" role="group" aria-labelledby="ece-quiz-qtext">
        <span id="ece-quiz-qtext" class="ece-sr-only">${escapeHtml(q.text)}</span>
        ${optionsHtml}
      </div>
    `;

    bodyRoot.querySelectorAll('[data-ece-option]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number((btn as HTMLElement).dataset.eceOption);
        if (!Number.isFinite(idx)) return;
        if (this.questionIndex === 0 && !this.quizStartLogged) {
          this.quizStartLogged = true;
          /* GTM: first engagement on Q1 (distinct from modal impression). */
          pushDataLayer({
            event: 'ece_quiz_start',
            article_url: this.opts.config.articleUrl,
          });
        }
        this.selections[this.questionIndex] = idx;
        this.goToNextQuestionOrPathfinder();
      });
    });

    this.trackQuestionView();
  }

  /** Advances `questionIndex` or calls `enterPathfinder` after the last in-widget answer. */
  private goToNextQuestionOrPathfinder(): void {
    if (this.selections[this.questionIndex] === null) return;
    if (this.questionIndex < 6) {
      this.questionIndex += 1;
      this.renderQuestionsView();
      this.focusFirstAnswerOption();
    } else {
      this.enterPathfinder();
    }
  }

  /** Moves keyboard focus to the first answer button after re-render. */
  private focusFirstAnswerOption(): void {
    queueMicrotask(() => {
      const el = this.mainStage().querySelector('.ece-option') as HTMLElement | null;
      el?.focus();
    });
  }

  private isShellVisible(): boolean {
    return !this.shell().hidden;
  }

  /** GTM: one event per visible question step (1–7). */
  private trackQuestionView(): void {
    if (!this.isShellVisible()) return;
    /* GTM: question step shown (modal open or advance); question_number 1–7. */
    pushDataLayer({
      event: 'ece_question_view',
      question_number: this.questionIndex + 1,
      article_url: this.opts.config.articleUrl,
    });
  }

  /** Replaces the main stage with the Pathfinder iframe after Q7; subscribes to `PATH_COMPLETE`. */
  private enterPathfinder(): void {
    this.funnelPhase = 'pathfinder';
    const stage = this.mainStage();
    const layer = this.pathfinderPreloadHost();
    if (!layer) return;

    stage.hidden = false;

    if (!layer.querySelector('.ece-pathfinder-root')) {
      const src = this.getPathfinderIframeSrc();
      layer.innerHTML = `
      <div class="ece-pathfinder-root">
        <div class="ece-iframe-wrap">
          <iframe
            class="ece-iframe"
            title="Continue your quiz"
            src="${escapeAttr(src)}"
            loading="eager"
            referrerpolicy="no-referrer-when-downgrade"
            tabindex="-1"
          ></iframe>
        </div>
      </div>
    `;
    }

    layer.classList.add('ece-pathfinder-layer--active');
    layer.setAttribute('aria-hidden', 'false');
    stage.setAttribute('aria-hidden', 'true');

    layer.querySelector('iframe.ece-iframe')?.removeAttribute('tabindex');

    const { pathfinderAllowedOrigins } = this.opts;

    /* GTM: Q1–7 complete; Pathfinder iframe mounted (transition to extended flow / completion baseline). */
    pushDataLayer({
      event: 'ece_pathfinder_handoff',
      article_url: this.opts.config.articleUrl,
    });

    this.pathfinderUnsub?.();
    this.pathfinderUnsub = subscribePathfinderComplete(pathfinderAllowedOrigins, () => {
      this.showResults();
    });
  }

  /** Swaps iframe for archetype card from `scoreSelections` / `winningResultFromScores`. */
  private showResults(): void {
    this.funnelPhase = 'results';
    this.pathfinderUnsub?.();
    this.pathfinderUnsub = null;

    const stage = this.mainStage();
    this.clearPathfinderPreload();
    stage.removeAttribute('aria-hidden');
    stage.hidden = false;

    const scores = scoreSelections(this.opts.config.questions, this.selections);
    const winner = winningResultFromScores(this.opts.config, scores);

    if (!winner) {
      stage.innerHTML = `
        <div class="ece-results-inner">
          <div class="ece-results-card ece-results-card--soft">
            <p class="ece-results-kicker">Thanks for completing the quiz</p>
            <h3 class="ece-results-title ece-results-title--plain">Thank you for reflecting with us</h3>
            <p class="ece-results-body">We couldn’t map a single style from your answers this time—but your curiosity still matters. Come back to this article anytime.</p>
          </div>
          <button type="button" class="ece-btn ece-btn--primary ece-btn--results-done" data-ece-done>Done</button>
        </div>
      `;
    } else {
      stage.innerHTML = `
        <div class="ece-results-inner">
          <div class="ece-results-card ece-results-card--highlight">
            <div class="ece-results-accent" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
            <p class="ece-results-kicker">Your archetype</p>
            <h3 class="ece-results-title">${escapeHtml(winner.name)}</h3>
            <p class="ece-results-body">${escapeHtml(winner.description)}</p>
          </div>
          <button type="button" class="ece-btn ece-btn--primary ece-btn--results-done" data-ece-done>Done</button>
        </div>
      `;
    }

    stage.querySelector('[data-ece-done]')?.addEventListener('click', () => this.dismiss());
    (stage.querySelector('[data-ece-done]') as HTMLElement)?.focus();
  }

  /** Cycles Tab within the dialog panel while open (shadow-aware activeElement). */
  private focusTrapStart(): void {
    const panel = this.opts.mount.querySelector('.ece-panel');
    /* Land on Q1’s first answer, not the close control — avoids a brief :focus-visible ring on open. */
    const firstOption = this.opts.mount.querySelector('.ece-option') as HTMLElement | null;
    const closeBtn = this.opts.mount.querySelector('[data-ece-close]') as HTMLElement | null;
    (firstOption ?? closeBtn)?.focus();

    const handler: EventListener = (ev: Event) => {
      if (!(ev instanceof KeyboardEvent) || ev.key !== 'Tab' || !panel) return;
      const focusables = getFocusable(panel);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const rootNode = this.opts.mount.getRootNode();
      const active =
        rootNode instanceof ShadowRoot ? rootNode.activeElement : document.activeElement;
      if (ev.shiftKey) {
        if (active === first || !focusables.includes(active as HTMLElement)) {
          ev.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        ev.preventDefault();
        first.focus();
      }
    };
    panel?.addEventListener('keydown', handler);
  }
}

/** Visible, enabled focus targets inside the dialog for trap logic. */
function getFocusable(root: Element): HTMLElement[] {
  const sel = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll(sel)).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && !el.hasAttribute('disabled') && !el.hidden,
  );
}

/** Sanitizes text injected into `innerHTML` templates. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Like `escapeHtml` plus single quotes for attribute contexts (e.g. iframe `src` in HTML string). */
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, '&#39;');
}
